<template>
  <div class="archiv-container">

    <!-- Filter-Sektion -->
    <div class="filters">
      <div class="left-filters">
        <div class="entries-wrapper">
          <label>Zeige</label>
          <select v-model.number="itemsPerPage" class="entries-select">
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="25">25</option>
            <option :value="-1">Alle</option>
          </select>
          <span>Einträge</span>
        </div>

        <div class="search-wrapper">
          <span class="material-symbols-outlined search-icon">search</span>
          <input v-model="search" placeholder="Suchbegriff" class="search-input" />
        </div>
      </div>
    </div>

    <!-- Projekttabelle -->
    <table v-if="paginatedProjectsList.length" class="projects-table">
      <thead>
        <tr>
          <th>Titel</th>
          <th>Startdatum</th>
          <th>Enddatum</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="project in paginatedProjectsList" :key="project.projektId">
          <td
            @click="loadDocumentsForProject(project.titel)"
            style="cursor: pointer; color: #5841ee; text-decoration: underline;"
          >
            {{ displayText(project.titel, project.projektId) }}
          </td>
          <td>{{ formatDate(project.startdatum) }}</td>
          <td>{{ formatDate(project.enddatum) }}</td>
        </tr>
      </tbody>
    </table>

    <p v-else class="no-projects">Es wurden noch keine Einträge archiviert.</p>

    <!-- Pagination -->
    <div v-if="totalPages > 1" class="pagination">
      <button @click="goToPrevPage" :disabled="currentPage === 1">Letztes</button>
      <button
        v-for="page in pages"
        :key="page"
        @click="changePage(page)"
        :class="['page-button', { 'active-page': page === currentPage }]"
      >
        {{ page }}
      </button>
      <button @click="goToNextPage" :disabled="currentPage === totalPages">Nächstes</button>
    </div>

    <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>

    <!-- Dokumente-Popup -->
    <div v-if="showModal" class="modal-overlay" @click.self="showModal = false">
      <div class="modal-content">
        <!-- NEUES X-Icon zum Schließen -->
        <button class="modal-close" @click="showModal = false">
          <span class="material-symbols-outlined">close</span>
        </button>

        <h2>Dokumente zum Projekt: {{ selectedProjectTitle }}</h2>
        <br>
        <table class="projects-table">
          <thead>
            <tr>
              <th>Dateiname</th>
              <th>Hochladedatum</th>
              <th>Ersteller</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="doc in relatedDocuments" :key="doc.dokumentId">
              <td>
                <a
                  :href="`${backendUrl}/api/documents/download/${doc.dokumentId}`"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="download-link"
                >
                  {{ doc.dateiname }}
                </a>
              </td>
              <td>{{ formatDate(doc.hochladungsdatum) }}</td>
              <td>{{ doc.erstellerSamAccountName }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'

export default {
  name: 'ArchivierteProjekte',
  setup () {
    const projectsList = ref([])
    const search = ref('')
    const itemsPerPage = ref(10)
    const currentPage = ref(1)
    const errorMessage = ref(null)
    const backendUrl = process.env.VUE_APP_BACKEND

    const showModal = ref(false)
    const selectedProjectTitle = ref('')
    const relatedDocuments = ref([])

    async function fetchProjects () {
      try {
        errorMessage.value = null
        const resp = await fetch(`${process.env.VUE_APP_BACKEND}/diplomdb/api/projects`, {
          method: 'GET',
          credentials: 'include'
        })
        if (!resp.ok) throw new Error('fetchProjects nicht ok, Status=' + resp.status)

        const data = await resp.json()
        const today = new Date()
        today.setHours(0, 0, 0, 0)

        projectsList.value = data.filter(p => {
          if (!p.enddatum) return false
          const end = new Date(p.enddatum)
          end.setHours(0, 0, 0, 0)
          return end < today
        })

        if (currentPage.value > totalPages.value) currentPage.value = 1
      } catch (err) {
        errorMessage.value = 'FEHLER: ' + err.message
      }
    }

    async function loadDocumentsForProject (title) {
      try {
        const resp = await fetch(`${process.env.VUE_APP_BACKEND}/diplomdb/api/documents`, {
          method: 'GET',
          credentials: 'include'
        })
        if (!resp.ok) throw new Error('Fehler beim Laden der Dokumente: ' + resp.status)
        const data = await resp.json()
        relatedDocuments.value = data.filter(doc => doc.diplomarbeitTitel === title)
        selectedProjectTitle.value = title
        showModal.value = true
      } catch (err) {
        errorMessage.value = 'Dokumente konnten nicht geladen werden: ' + err.message
      }
    }

    onMounted(fetchProjects)

    function formatDate (val) {
      if (!val) return '-'
      const d = new Date(val)
      const day = String(d.getDate()).padStart(2, '0')
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const year = d.getFullYear()
      return `${day}.${month}.${year}`
    }

    function displayText (value, fallback) {
      return value && value.trim() !== '' ? value :
        (fallback !== undefined && fallback !== null ? String(fallback) : '-')
    }

    const filteredProjectsList = computed(() => {
      if (!search.value) return projectsList.value
      const term = search.value.toLowerCase()
      return projectsList.value.filter(p =>
        (p.titel || '').toLowerCase().includes(term) ||
        (p.startdatum || '').toLowerCase().includes(term) ||
        (p.enddatum || '').toLowerCase().includes(term) ||
        String(p.projektId).includes(term)
      )
    })

    const totalPages = computed(() =>
      itemsPerPage.value < 0 ? 1 :
      Math.ceil(filteredProjectsList.value.length / itemsPerPage.value)
    )

    const paginatedProjectsList = computed(() => {
      if (itemsPerPage.value < 0) return filteredProjectsList.value
      const startIndex = (currentPage.value - 1) * itemsPerPage.value
      return filteredProjectsList.value.slice(startIndex, startIndex + itemsPerPage.value)
    })

    const pages = computed(() => {
      const arr = []
      for (let i = 1; i <= totalPages.value; i++) arr.push(i)
      return arr
    })

    function changePage (page) { currentPage.value = page }
    function goToPrevPage () { if (currentPage.value > 1) currentPage.value-- }
    function goToNextPage () { if (currentPage.value < totalPages.value) currentPage.value++ }

    return {
      search,
      itemsPerPage,
      currentPage,
      errorMessage,
      paginatedProjectsList,
      pages,
      totalPages,
      changePage,
      goToPrevPage,
      goToNextPage,
      formatDate,
      displayText,
      showModal,
      selectedProjectTitle,
      relatedDocuments,
      loadDocumentsForProject
    }
  }
}
</script>

<style scoped>
/*****************************************
 * Allgemeines Layout
 *****************************************/
.archiv-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 90vh;
  font-family: 'DM Sans', sans-serif;
  padding: 20px;
  max-width: 1100px;
  margin: 0 auto;
}
.filters {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 65%;
  margin-bottom: 20px;
}
.left-filters {
  display: flex;
  align-items: center;
  gap: 20px;
}
.entries-wrapper {
  display: flex;
  align-items: center;
  gap: 6px;
}
.entries-select {
  padding: 4px;
  border-radius: 4px;
  border: 1px solid #ccc;
  font-family: 'DM Sans', sans-serif;
  cursor: pointer;
}
.search-wrapper {
  position: relative;
  display: flex;
  width: 300px;
}
.search-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
  font-size: 20px;
  pointer-events: none;
}
.search-input {
  width: 100%;
  padding: 8px 8px 8px 35px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  font-family: 'DM Sans', sans-serif;
}
.projects-table {
  width: 100%;
  border-collapse: collapse;
  border-radius: 8px;
  overflow: hidden;
  font-size: 14px;
  background-color: #fff;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}
.projects-table thead tr {
  background-color: #f4f4f4;
}
.projects-table th,
.projects-table td {
  padding: 16px;
  text-align: center;
  border-bottom: 1px solid #ddd;
  font-family: 'DM Sans', sans-serif;
}
.projects-table tbody tr:nth-child(odd) {
  background-color: #F7F6FE;
}
.projects-table tbody tr:nth-child(even) {
  background-color: #ffffff;
}
.projects-table tbody tr:hover {
  background-color: #eaeafc;
}
.no-projects {
  text-align: center;
  margin: 20px 0;
  font-size: 16px;
  color: #777;
  font-family: 'DM Sans', sans-serif;
}
.error-message {
  text-align: center;
  margin-top: 20px;
  font-size: 16px;
  color: red;
  font-family: 'DM Sans', sans-serif;
}

/*****************************************
 * Pagination
 *****************************************/
.pagination {
  margin-top: 20px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: 'DM Sans', sans-serif;
}
.pagination button {
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  background-color: #e1e1e1;
  color: #000;
  font-weight: normal;
  transition: background-color 0.3s;
  border: none;
}
.pagination button:hover {
  background-color: #d4d4d4;
}
.pagination button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.page-button.active-page {
  background-color: #5841ee;
  color: #fff;
  font-weight: bold;
}
.page-button.active-page:hover {
  background-color: #4a35d2;
}

/*****************************************
 * Modal + Downloadlink
 *****************************************/
.modal-overlay {
  position: fixed;
  top: 0; left: 0;
  width: 100vw; height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: flex; align-items: center; justify-content: center;
  z-index: 1000;
}
.modal-content {
  background: white;
  padding: 30px;
  border-radius: 12px;
  max-width: 700px;
  width: 90%;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
  position: relative; /* wichtig für absolut positioniertes X */
}

/* Neues Schließen (X) oben rechts */
.modal-close {
  position: absolute;
  top: 12px;
  right: 12px;
  background: transparent;
  border: none;
  padding: 4px;
  cursor: pointer;
}
.modal-close .material-symbols-outlined {
  font-size: 24px;
  color: #666;
}
.modal-close:hover .material-symbols-outlined {
  color: #000;
}

.download-link {
  color: #5841ee;
  text-decoration: underline;
  cursor: pointer;
}
.download-link:hover {
  color: #4a35d2;
}
</style>
